// ===================================================
// ФАЙЛ: frontend/src/services/index.js
// ✅ ИСПРАВЛЕНО: API сервисы приведены в соответствие с бэкендом
// ===================================================
import axiosInstance from 'utils/axios';
import { API_ENDPOINTS } from 'utils/constants';
import { apiRejectPayload } from 'utils/apiRejectPayload';
import { devLogApiError } from 'utils/devLogApiError';
import SUPPLIERS_API from './suppliers';
import companiesService from './companies.service';
import notificationService from './notificationService';
import productGroupsService from './productGroups';

// Алиас для совместимости
const axios = axiosInstance;

// =====================================
// БАЗОВЫЙ КЛАСС ДЛЯ API СЕРВИСОВ
// =====================================
class BaseAPIService {
  /**
   * Обработка ошибок API
   */
  handleError(error) {
    // Superseded reads are canceled intentionally; keep their identity for callers.
    if (error?.name === 'AbortError' || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') throw error;
    const { message: errorMessage } = apiRejectPayload(error);

    devLogApiError('сервисы: BaseAPIService.handleError', error);

    throw new Error(errorMessage);
  }

  /**
   * Замена параметров в URL
   */
  replaceUrlParams(url, params) {
    return url.replace(/:(\w+)/g, (match, key) => {
      return params[key] || match;
    });
  }

  /**
   * Обработка ответа от API (стандартная структура бэкенда)
   */
  handleResponse(response) {
    const data = response?.data;
    if (data && typeof data === 'object') {
      if (data.success === false) {
        throw new Error(data.error || 'Ошибка запроса к API');
      }
      if (data.success === true) {
        return data;
      }
      return data;
    }
    return data;
  }
}

// =====================================
// AUTHENTICATION API
// =====================================
class AuthAPI extends BaseAPIService {
  /**
   * Вход в систему
   */
  async login(email, password, rememberMe = false) {
    try {
      const response = await axios.post(API_ENDPOINTS.LOGIN, {
        email,
        password,
        remember_me: rememberMe,
      });

      const result = this.handleResponse(response);

      if (result.success) {
        const { token, refreshToken, data } = result;

        // Сохраняем токены через axios instance
        axios.setToken(token, refreshToken);

        // Сохраняем данные пользователя
        this.setUser(data.user);

        return { token, refreshToken, user: data.user };
      }

      throw new Error(result.error || 'Не удалось выполнить вход');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Регистрация
   * Fail-closed: регистрация разрешена только через UAM Register с accepted_consent_types.
   */
  async register() {
    const uamUrl = (process.env.REACT_APP_UAM_URL || 'https://auth.markbase.ru').replace(/\/+$/, '');
    const redirectUrl = `${uamUrl}/register`;

    if (typeof window !== 'undefined') {
      const returnUrl = `${window.location.origin}/auth/callback?to=${encodeURIComponent('/dashboard')}`;
      window.location.href = `${redirectUrl}?return_url=${encodeURIComponent(returnUrl)}`;
    }

    const error = new Error('Регистрация выполняется только через Маркбэйс id с обязательными согласиями.');
    error.code = 'UAM_REGISTRATION_REQUIRED';
    error.redirect_url = redirectUrl;
    throw error;
  }

  /**
   * Выход из системы
   */
  async logout() {
    try {
      await axios.post(API_ENDPOINTS.LOGOUT);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Ошибка выхода:', apiRejectPayload(error).message);
      }
    } finally {
      this.clearAuth();
    }
  }

  /**
   * Обновление токена
   */
  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        throw new Error('Отсутствует токен обновления (refresh token)');
      }

      const response = await axios.post(API_ENDPOINTS.REFRESH, { refreshToken });
      const { token: newToken, refreshToken: newRefreshToken } = response.data;

      axios.setToken(newToken, newRefreshToken);

      return { token: newToken, refreshToken: newRefreshToken };
    } catch (error) {
      this.clearAuth();
      throw this.handleError(error);
    }
  }

  /**
   * Получение текущего пользователя
   */
  async getCurrentUser() {
    try {
      const response = await axios.get(API_ENDPOINTS.ME);
      const result = this.handleResponse(response);

      if (result.success) {
        const user = result.data.user;
        this.setUser(user);
        return user;
      }

      throw new Error(result.error || 'Не удалось получить данные пользователя');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Смена пароля
   */
  async changePassword(oldPassword, newPassword) {
    try {
      const response = await axios.post(API_ENDPOINTS.CHANGE_PASSWORD, {
        currentPassword: oldPassword,
        newPassword: newPassword,
      });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Восстановление пароля
   */
  async forgotPassword(email) {
    try {
      const response = await axios.post(API_ENDPOINTS.FORGOT_PASSWORD, { email });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Сброс пароля
   */
  async resetPassword(token, newPassword) {
    try {
      const response = await axios.post(API_ENDPOINTS.RESET_PASSWORD, {
        token,
        newPassword: newPassword,
      });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Проверка авторизации при загрузке приложения
   */
  async checkAuth() {
    try {
      if (!axios.isAuthenticated()) {
        return null;
      }

      const user = await this.getCurrentUser();
      return user;
    } catch (error) {
      this.clearAuth();
      return null;
    }
  }

  /**
   * Обновление данных пользователя
   */
  async updateUser(userData) {
    try {
      const response = await axios.put(API_ENDPOINTS.ME, userData);
      const result = this.handleResponse(response);

      if (result.success) {
        const user = result.data.user;
        this.setUser(user);
        return user;
      }

      throw new Error(result.error || 'Не удалось обновить данные пользователя');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Вспомогательные методы для работы с localStorage
  setUser(user) {
    localStorage.setItem('user', JSON.stringify(user));
  }

  getUser() {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  }

  clearAuth() {
    axios.clearTokens();
    localStorage.removeItem('user');
  }

  isAuthenticated() {
    return axios.isAuthenticated();
  }
}

// =====================================
// PRODUCTS API
// =====================================
class ProductsAPI extends BaseAPIService {
  /**
   * Получение списка товаров
   */
  async getProducts(params = {}, { signal } = {}) {
    try {
      // ❌ НЕ КЭШИРУЕМ: Списки товаров могут содержать цены и наличие - критически важные данные
      // Все данные о деньгах и наличии должны быть актуальными без кэша
      const cacheTtl = 0; // Отключаем кэширование для актуальности данных

      const response = await axios.get(API_ENDPOINTS.PRODUCTS, { params, cacheTtlMs: cacheTtl, signal, noDedup: Boolean(signal) });
      const result = this.handleResponse(response);

      // ✅ ИСПРАВЛЕНО: Правильная обработка ответа - возвращаем данные даже если success не указан
      if (result && (result.success === true || result.success === undefined)) {
        return {
          data: result.data || [],
          products: result.data || result.products || [],
          pagination: result.pagination || {},
          total: result.pagination?.total ?? result.total ?? null
        };
      }

      throw new Error(result?.error || 'Не удалось загрузить список товаров');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Фоновая догрузка "тяжёлых" полей для списка товаров (остатки/цены и т.п.)
   * POST /api/products/bulk-fields
   */
  async getBulkFields({ ids = [], include = [], warehouse_id = null } = {}, { signal } = {}) {
    try {
      const response = await axios.post(`${API_ENDPOINTS.PRODUCTS}/bulk-fields`, {
        ids: Array.isArray(ids) ? ids : [],
        include: Array.isArray(include) ? include : [],
        warehouse_id: warehouse_id || null,
      }, { signal });
      const result = this.handleResponse(response);
      return result?.data || [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Получить быструю статистику по товарам */
  async getStats({ signal } = {}) {
    try {
      // Freshness is server-owned by the rebuildable read model. The client keeps
      // only in-flight dedupe and forwards cancellation during workspace switches.
      const response = await axios.get(`${API_ENDPOINTS.PRODUCTS}/stats`, { cacheTtlMs: 0, signal });
      const result = this.handleResponse(response);
      if (result.success) return result.data;
      throw new Error(result.error || 'Не удалось загрузить статистику по товарам');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Поставщики товара: список маппингов */
  async getSupplierMappings(productId) {
    try {
      const response = await axios.get(`/api/products/${productId}/supplier-mappings`);
      const result = this.handleResponse(response);
      return result?.data || result || [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Добавить/обновить маппинг поставщика для товара */
  async upsertSupplierMapping(productId, payload) {
    try {
      const response = await axios.post(`/api/products/${productId}/supplier-mappings`, payload);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Удалить маппинг поставщика для товара */
  async deleteSupplierMapping(productId, supplierId, externalId) {
    try {
      const response = await axios.delete(`/api/products/${productId}/supplier-mappings/${supplierId}/${externalId}`);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Получить информацию о поставщиках для товара */
  async getSupplierInfo(productId) {
    try {
      const response = await axios.get(`/api/products/${productId}/supplier-info`);
      const result = this.handleResponse(response);
      return result?.data || result || [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Точечная синхронизация товара с поставщиком */
  async syncProductFromSupplier(productId, { supplier_id, warehouse_id = null, mode = 'both' }) {
    try {
      const response = await axios.post(`/api/products/${productId}/sync-supplier`, { supplier_id, warehouse_id, mode });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение товара по ID
   * @param {string} id
   * @param {{ cacheTtlMs?: number } & Record<string, unknown>} [axiosOpts] — cacheTtlMs 0 или undefined: без кеша клиента для «свежей» карточки
   */
  async getProduct(id, axiosOpts = {}) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.PRODUCT_DETAILS, { id });
      const { cacheTtlMs = 60 * 1000, ...rest } = axiosOpts && typeof axiosOpts === 'object' ? axiosOpts : {};
      const response = await axios.get(url, { ...rest, cacheTtlMs });
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось загрузить товар');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Создание товара
   */
  async createProduct(productData) {
    try {
      const response = await axios.post(API_ENDPOINTS.PRODUCTS, productData);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось создать товар');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Обновление товара
   */
  async updateProduct(id, productData) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.PRODUCT_DETAILS, { id });
      const response = await axios.put(url, productData);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось сохранить товар');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Удаление товара
   */
  async deleteProduct(id) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.PRODUCT_DETAILS, { id });
      const response = await axios.delete(url);
      const result = this.handleResponse(response);

      if (result.success) {
        return result;
      }

      throw new Error(result.error || 'Не удалось удалить товар');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение категорий
   */
  async getCategories() {
    try {
      const response = await axios.get(API_ENDPOINTS.PRODUCTS_CATEGORIES, { cacheTtlMs: 10 * 60 * 1000 });
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось загрузить категории');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение списка брендов
   */
  async getBrands(params = {}) {
    try {
      const response = await axios.get(API_ENDPOINTS.BRANDS, { params, cacheTtlMs: 10 * 60 * 1000 });
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось загрузить бренды');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Создание категории
   */
  async createCategory(categoryData) {
    try {
      const response = await axios.post(API_ENDPOINTS.PRODUCTS_CATEGORIES, categoryData);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось создать категорию');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Обновление категории
   */
  async updateCategory(id, categoryData) {
    try {
      const url = `${API_ENDPOINTS.PRODUCTS_CATEGORIES}/${id}`;
      const response = await axios.put(url, categoryData);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось сохранить категорию');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Удаление категории
   */
  async deleteCategory(id) {
    try {
      const url = `${API_ENDPOINTS.PRODUCTS_CATEGORIES}/${id}`;
      const response = await axios.delete(url);
      const result = this.handleResponse(response);

      if (result.success) {
        return result;
      }

      throw new Error(result.error || 'Не удалось удалить категорию');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Создание бренда
   */
  async createBrand(brandData) {
    try {
      const response = await axios.post(API_ENDPOINTS.BRANDS, brandData);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось создать бренд');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Обновление бренда
   */
  async updateBrand(id, brandData) {
    try {
      const url = `${API_ENDPOINTS.BRANDS}/${id}`;
      const response = await axios.put(url, brandData);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось сохранить бренд');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Удаление бренда
   */
  async deleteBrand(id) {
    try {
      const url = `${API_ENDPOINTS.BRANDS}/${id}`;
      const response = await axios.delete(url);
      const result = this.handleResponse(response);

      if (result.success) {
        return result;
      }

      throw new Error(result.error || 'Не удалось удалить бренд');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение атрибутов
   */
  async getAttributes() {
    try {
      const response = await axios.get(API_ENDPOINTS.DICTIONARIES_ATTRIBUTES, { cacheTtlMs: 10 * 60 * 1000 });
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data || [];
      }

      throw new Error(result.error || 'Не удалось загрузить атрибуты');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Создание атрибута
   */
  async createAttribute(attributeData) {
    try {
      const payload = { name: attributeData.name, is_required: !!attributeData.is_required, is_active: attributeData.is_active !== false };
      const response = await axios.post(API_ENDPOINTS.DICTIONARIES_ATTRIBUTES, payload);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось создать атрибут');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Обновление атрибута
   */
  async updateAttribute(id, attributeData) {
    try {
      const url = `${API_ENDPOINTS.DICTIONARIES_ATTRIBUTES}/${id}`;
      const payload = { name: attributeData.name, is_required: attributeData.is_required, is_active: attributeData.is_active };
      const response = await axios.put(url, payload);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось сохранить атрибут');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Удаление атрибута
   */
  async deleteAttribute(id) {
    try {
      const url = `${API_ENDPOINTS.DICTIONARIES_ATTRIBUTES}/${id}`;
      const response = await axios.delete(url);
      const result = this.handleResponse(response);

      if (result.success) {
        return result;
      }

      throw new Error(result.error || 'Не удалось удалить атрибут');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Импорт товаров
   */
  async importProducts(file, options = {}) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      Object.keys(options).forEach(key => {
        formData.append(key, options[key]);
      });

      const response = await axios.post(API_ENDPOINTS.PRODUCTS_IMPORT, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Запуск фонового импорта из интеграции (RS24)
   */
  async startBackgroundImport({ supplier_id, brand_ids = [], warehouse_ids = [], confirm_full_import = false } = {}) {
    try {
      const response = await axios.post(API_ENDPOINTS.PRODUCT_IMPORT_IMPORT, {
        supplier_id,
        brand_ids,
        warehouse_ids,
        confirm_full_import
      });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Экспорт master-каталога (PRODUCTS_EXPORT), не выгрузка кэша витрины Shop.
   */
  async exportProducts(params = {}) {
    try {
      const response = await axios.get(API_ENDPOINTS.PRODUCTS_EXPORT, {
        params,
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Массовое обновление товаров
   */
  async bulkUpdateProducts(productIds, updates) {
    try {
      const response = await axios.post(API_ENDPOINTS.PRODUCTS_BULK_UPDATE, {
        product_ids: productIds,
        updates
      });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Массовое удаление товаров (архивирование)
   */
  async bulkDeleteProducts(productIds) {
    try {
      const response = await axios.post(API_ENDPOINTS.PRODUCTS_BULK_DELETE, {
        product_ids: productIds
      });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Массовое восстановление товаров из архива
   */
  async bulkRestoreProducts(productIds) {
    try {
      const response = await axios.post('/api/products/bulk-restore', {
        product_ids: productIds
      });
      // Обрабатываем как успех даже при статусе 207 (Multi-Status - частичный успех)
      if (response.status === 207 || response.status === 200) {
        return { data: response.data, status: response.status };
      }
      return this.handleResponse(response);
    } catch (error) {
      // Если это 207 статус - это частичный успех, не ошибка
      if (error?.response?.status === 207) {
        return { data: error.response.data, status: 207 };
      }
      throw this.handleError(error);
    }
  }

  /**
   * Массовое полное удаление товаров из БД (необратимое)
   */
  async bulkPermanentDeleteProducts(productIds) {
    try {
      const response = await axios.post('/api/products/bulk-permanent-delete', {
        product_ids: productIds
      });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Массовое редактирование названий товаров
   */
  async bulkEditNames(payload) {
    try {
      const response = await axios.post('/api/products/bulk-edit-names', payload);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ===== RS24 / Product-import helpers (dictionary import flow) =====
  async harvestDictionaries(supplierId, options = {}) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.PRODUCT_IMPORT_HARVEST_DICTIONARIES,
        { supplier_id: supplierId, options }
      );
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async fetchSupplierBrandsAsync(supplierId, options = {}) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.PRODUCT_IMPORT_FETCH_BRANDS,
        { supplier_id: supplierId, options }
      );
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async fetchSupplierCategoriesAsync(supplierId, options = {}) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.PRODUCT_IMPORT_FETCH_CATEGORIES,
        { supplier_id: supplierId, options }
      );
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getImportStatus(sessionId) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.PRODUCT_IMPORT_STATUS, { sessionId });
      const response = await axios.get(url);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getSupplierExternalCategories(supplierId) {
    try {
      const url = this.replaceUrlParams(
        API_ENDPOINTS.PRODUCT_IMPORT_SUPPLIER_CATEGORIES,
        { supplierId }
      );
      const response = await axios.get(url);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getSupplierExternalBrands(supplierId) {
    try {
      const url = this.replaceUrlParams(
        API_ENDPOINTS.PRODUCT_IMPORT_SUPPLIER_BRANDS,
        { supplierId }
      );
      const response = await axios.get(url);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getMappingStats(supplierId) {
    try {
      const response = await axios.get(`/api/product-import/mapping-stats/${supplierId}`);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getImportSessions(params = {}) {
    try {
      const response = await axios.get(API_ENDPOINTS.PRODUCT_IMPORT_SESSIONS, { params });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Документы товара
  async getProductDocuments(productId) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.PRODUCT_DETAILS, { id: productId }) + '/documents';
      const response = await axios.get(url);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteProductDocument(productId, docId) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.PRODUCT_DETAILS, { id: productId }) + `/documents/${docId}`;
      const response = await axios.delete(url);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Единицы товара (CRUD)
  async getProductUnits(productId) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.PRODUCT_DETAILS, { id: productId }) + '/units';
      const response = await axios.get(url);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createProductUnit(productId, data) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.PRODUCT_DETAILS, { id: productId }) + '/units';
      const response = await axios.post(url, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateProductUnit(productId, unitId, data) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.PRODUCT_DETAILS, { id: productId }) + `/units/${unitId}`;
      const response = await axios.put(url, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteProductUnit(productId, unitId) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.PRODUCT_DETAILS, { id: productId }) + `/units/${unitId}`;
      const response = await axios.delete(url);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Запустить полный импорт ("Скачать все")
   */
  async startFullImport(supplierId, { brand_ids = [], warehouse_ids = [] } = {}) {
    try {
      const payload = { supplier_id: supplierId, brand_ids, warehouse_ids, confirm_full_import: true };
      const response = await axios.post(API_ENDPOINTS.PRODUCT_IMPORT_IMPORT, payload);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getSupplierCatalog(supplierId, params = {}) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.SUPPLIER_CATALOG, { supplierId });
      const response = await axios.get(url, { params });
      const result = this.handleResponse(response);
      if (result.success) return result.data;
      throw new Error(result.error || 'Не удалось загрузить каталог поставщика');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Предварительный подсчет данных для импорта
   */
  async calculateImport(payload) {
    try {
      const response = await axios.post(API_ENDPOINTS.PRODUCT_IMPORT_CALCULATE, payload);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение остатков товара по складам
   */
  async getProductStock(productId) {
    try {
      const url = this.replaceUrlParams(`${API_ENDPOINTS.PRODUCTS}/:id/stock`, { id: productId });
      const response = await axios.get(url);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Резервы товара (sklad.stock_reservations): кто / склад / количество
   */
  async getProductStockReservations(productId, params = {}) {
    try {
      const response = await axios.get(`/api/products/${productId}/stock-reservations`, { params });
      const result = this.handleResponse(response);
      return result?.data || [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение остатков товара от поставщиков (агрегировано по supplier mappings)
   */
  async getProductSupplierStocks(productId) {
    try {
      const url = this.replaceUrlParams(`${API_ENDPOINTS.PRODUCTS}/:id/supplier-stocks`, { id: productId });
      const response = await axios.get(url);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Обновление остатков товара на складе
   */
  async updateProductStock(productId, warehouseId, stockData) {
    try {
      const url = this.replaceUrlParams(`${API_ENDPOINTS.PRODUCTS}/:id/stock/:warehouseId`, {
        id: productId,
        warehouseId: warehouseId
      });
      const response = await axios.put(url, stockData);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Цены поставщиков по товару (для UI карточки/цен) */
  async getSupplierPrices(productId, params = {}) {
    try {
      // Извлекаем detailed из params, остальные параметры (включая _t для обхода кэша) передаем как есть
      const { detailed = true, ...otherParams } = params;
      const queryParams = { detailed: detailed ? 1 : 0, ...otherParams };
      const response = await axios.get(`/api/products/${productId}/supplier-prices`, {
        params: queryParams
      });
      const result = this.handleResponse(response);
      return result?.data || [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Получить ленту событий товара (движения/резервы/продажи/партии/маркировка/аудит) */
  async getProductEvents(productId, params = {}) {
    try {
      const response = await axios.get(`/api/products/${productId}/events`, { params });
      const result = this.handleResponse(response);
      return result?.data || [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Установить primary supplier для авто-цены (через supplier mappings) */
  async setPrimarySupplierMapping(productId, supplierId) {
    try {
      const response = await axios.put(`/api/products/${productId}/supplier-mappings/primary`, {
        supplier_id: supplierId
      });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

// =====================================
// PRICES API (единая логика цен)
// =====================================
class PricesAPI extends BaseAPIService {
  async getMeta() {
    try {
      const response = await axios.get('/api/prices/meta');
      const result = this.handleResponse(response);
      return result?.data || null;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getProductPrices(productId, params = {}) {
    try {
      // Извлекаем priceType из params, остальные параметры (включая _t для обхода кэша) передаем как есть
      const { priceType = 'retail', ...otherParams } = params;
      const queryParams = { priceType, ...otherParams };
      // ❌ НЕ КЭШИРУЕМ: Цены - критически важные данные
      const response = await axios.get(`/api/prices/product/${productId}`, {
        params: queryParams,
        cacheTtlMs: 0, // Явно отключаем кэширование
        silent404: true, // empty Hub is a valid state, not a global toast
      });
      const result = this.handleResponse(response);
      return result?.data || [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateGlobalProductPrices(productId, payload) {
    try {
      const response = await axios.put(`/api/prices/product/${productId}`, payload);
      const result = this.handleResponse(response);
      return result?.data || result;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateWarehouseProductPrices(productId, warehouseId, payload) {
    try {
      const response = await axios.put(`/api/prices/product/${productId}/warehouse/${warehouseId}`, payload);
      const result = this.handleResponse(response);
      return result?.data || result;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async explainProductPrice(productId, params = {}) {
    try {
      // Извлекаем warehouseId из params, остальные параметры (включая _t для обхода кэша) передаем как есть
      const { warehouseId = null, ...otherParams } = params;
      const queryParams = { warehouseId: warehouseId || null, ...otherParams };
      const response = await axios.get(`/api/prices/explain/product/${productId}`, {
        params: queryParams
      });
      const result = this.handleResponse(response);
      return result?.data || null;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async explainProductsPrices(productIds = [], params = {}, { signal } = {}) {
    try {
      const { warehouseId = null } = params || {};
      const response = await axios.post('/api/prices/explain/products', {
        productIds: Array.isArray(productIds) ? productIds : [],
        warehouseId: warehouseId || null
      }, { signal });
      const result = this.handleResponse(response);
      return result?.data || { items: [] };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getDisplayPrice(productId, params = {}) {
    try {
      const { warehouseId = null, withVat = true, ...otherParams } = params || {};
      const response = await axios.get(`/api/prices/display/${productId}`, {
        params: {
          warehouseId: warehouseId || undefined,
          withVat,
          ...otherParams,
        },
        cacheTtlMs: 0,
        silent404: true,
      });
      const result = this.handleResponse(response);
      return result?.data || null;
    } catch (error) {
      if (error?.response?.status === 404) return null;
      throw this.handleError(error);
    }
  }

  async getConfigurationHealth() {
    try {
      const response = await axios.get('/api/prices/configuration-health', { cacheTtlMs: 0 });
      const result = this.handleResponse(response);
      return result?.data || null;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** P-IMPROVE-06 — read-only margin/beat/dirty analytics (no SoT write). */
  async getReadonlyAnalytics() {
    try {
      const response = await axios.get('/api/prices/readonly-analytics', { cacheTtlMs: 0 });
      const result = this.handleResponse(response);
      return result?.data || null;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getAudienceAssignments() {
    try {
      const response = await axios.get('/api/prices/audience-assignments', { cacheTtlMs: 0 });
      const result = this.handleResponse(response);
      return result?.data || { assignments: [] };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async upsertAudienceAssignment(audienceScope, payload) {
    try {
      const response = await axios.put(
        `/api/prices/audience-assignments/${encodeURIComponent(audienceScope)}`,
        payload,
      );
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getStackingPolicy() {
    try {
      const response = await axios.get('/api/prices/stacking-policy', { cacheTtlMs: 0 });
      const result = this.handleResponse(response);
      return result?.data || null;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateStackingPolicy(payload) {
    try {
      const response = await axios.put('/api/prices/stacking-policy', payload);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async quotePos(payload = {}) {
    try {
      const response = await axios.post('/api/prices/pos/quote', {
        channel: 'pos',
        items: payload.items || [],
        client_id: payload.clientId || payload.client_id || null,
        loyalty_card_id: payload.loyaltyCardId || payload.loyalty_card_id || null,
        warehouse_id: payload.warehouseId || payload.warehouse_id || null,
        shop_id: payload.shopId || payload.shop_id || null,
      }, { cacheTtlMs: 0 });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async verifyCompanyTaxProfile(payload) {
    try {
      const response = await axios.post('/api/prices/company-tax-profile/verify', payload);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async resetManual(productId, { warehouseId = null, fields = [] } = {}) {
    try {
      const response = await axios.post(`/api/prices/product/${productId}/reset-manual`, {
        warehouseId: warehouseId || null,
        fields: Array.isArray(fields) ? fields : []
      });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getDualAnchorStatus(productId) {
    try {
      const response = await axios.get(`/api/prices/product/${productId}/dual-anchor`, { cacheTtlMs: 0 });
      const result = this.handleResponse(response);
      return result?.data || null;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async lockDualAnchor(productId, payload = {}) {
    try {
      const response = await axios.post(`/api/prices/product/${productId}/dual-anchor/lock`, payload);
      const result = this.handleResponse(response);
      return result?.data || result;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async unlockDualAnchor(productId, payload = {}) {
    try {
      const response = await axios.post(`/api/prices/product/${productId}/dual-anchor/unlock`, payload);
      const result = this.handleResponse(response);
      return result?.data || result;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async resolveDualAnchor(productId, payload = {}) {
    try {
      const response = await axios.post(`/api/prices/product/${productId}/dual-anchor/resolve`, payload);
      const result = this.handleResponse(response);
      return result?.data || result;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getProductPriceHistory(productId, { warehouseId = null } = {}) {
    try {
      const response = await axios.get(`/api/prices/product/${productId}/history`, {
        params: { warehouseId: warehouseId || null }
      });
      const result = this.handleResponse(response);
      return result?.data || [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async previewConfigurator({ template, warehouseId = null, limit = 5 } = {}) {
    try {
      const response = await axios.post('/api/prices/configurator/preview', {
        template,
        warehouseId: warehouseId || null,
        limit
      });
      const result = this.handleResponse(response);
      return result?.data || { items: [] };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async recalculate({ productIds = [], priority = 'high', scope = 'all', warehouseIds = [], full_rebuild = false, reason = null } = {}) {
    try {
      const ids = Array.isArray(productIds) ? productIds : [];
      const payload = {
        productIds: ids,
        priority: priority || 'high'
      };
      if (scope && scope !== 'all') payload.scope = scope;
      if (Array.isArray(warehouseIds) && warehouseIds.length) payload.warehouseIds = warehouseIds;
      if (ids.length === 0 || full_rebuild) {
        payload.full_rebuild = true;
        payload.reason = reason || 'manual_recalculate';
      }
      const response = await axios.post('/api/prices/recalculate', payload);
      const result = this.handleResponse(response);
      return result?.data || result;
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

// =====================================
// CURRENCIES API
// =====================================
class CurrenciesAPI extends BaseAPIService {
  async getCurrencies() {
    try {
      // Курсы меняются редко — кэшируем дольше
      const response = await axios.get(API_ENDPOINTS.CURRENCIES, { cacheTtlMs: 10 * 60 * 1000 });
      const result = this.handleResponse(response);
      return result?.data || [];
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

// =====================================
// ORDERS API
// =====================================
class OrdersAPI extends BaseAPIService {
  async getOrders(params = {}) {
    try {
      const response = await axios.get(API_ENDPOINTS.ORDERS, { params });
      const result = this.handleResponse(response);

      if (result.success) {
        return {
          orders: result.data || [],
          pagination: result.pagination || {},
          total: result.pagination?.total || 0
        };
      }

      throw new Error(result.error || 'Не удалось загрузить заказы');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getOrdersStats() {
    try {
      const response = await axios.get(`${API_ENDPOINTS.ORDERS}/stats`, { cacheTtlMs: 30 * 1000 });
      const result = this.handleResponse(response);
      if (result.success) {
        return result.data || { total: 0, new: 0, processing: 0, delivered: 0 };
      }
      throw new Error(result.error || 'Не удалось загрузить статистику заказов');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getOrder(id) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.ORDER_DETAILS, { id });
      const response = await axios.get(url);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось загрузить заказ');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateOrderStatus(_id, _status, _comment = null) {
    throw new Error('Статус заказа изменяется только в модуле ORDERS.');
  }

  async bulkUpdateOrders(_orderIds, _updates) {
    throw new Error('Массовое изменение заказов выполняется только в модуле ORDERS.');
  }

  async exportOrders(params = {}) {
    try {
      const response = await axios.get(API_ENDPOINTS.ORDERS_EXPORT, {
        params,
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createOrder(_orderData) {
    throw new Error('Создание заказа выполняется только в модуле ORDERS.');
  }

  async updateOrder(_id, _orderData) {
    throw new Error('Редактирование заказа выполняется только в модуле ORDERS.');
  }

  async deleteOrder(_id) {
    throw new Error('Удаление заказа в legacy App запрещено; используйте модуль ORDERS.');
  }
}

// =====================================
// WAREHOUSES API
// =====================================
class WarehousesAPI extends BaseAPIService {
  async getWarehouses(params = {}) {
    try {
      // По умолчанию для UI-селектов используем словарь (минимальный, стабильный контракт).
      // Для страниц управления (нужны public_id, привязки к поставщикам, статистика и т.д.) используем full=true → /api/warehouses.
      const isFull = params?.full === true || String(params?.mode || '').toLowerCase() === 'full';
      const requestParams = { ...(params || {}) };
      delete requestParams.full;
      delete requestParams.mode;

      const response = await axios.get(isFull ? '/api/warehouses' : '/api/dictionaries/warehouses', { params: requestParams });
      const result = this.handleResponse(response);

      // Ожидаем { success, data: [...], pagination }. На всякий — поддержим legacy форматы.
      const list = Array.isArray(result?.data)
        ? result.data
        : (Array.isArray(result) ? result : (Array.isArray(result?.data?.data) ? result.data.data : []));
      const total = Number(
        result?.pagination?.total
        || result?.data?.pagination?.total
        || (Array.isArray(list) ? list.length : 0)
      );

      return { warehouses: list, total, stats: {}, pagination: result?.pagination || result?.data?.pagination || null };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getWarehouse(id) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.WAREHOUSE_DETAILS, { id });
      const response = await axios.get(url);
      const result = this.handleResponse(response);
      if (result.success) return result.data;
      throw new Error(result.error || 'Не удалось загрузить склад');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Geo SSOT: список городов для формы склада */
  async getCitiesAll(params = {}) {
    try {
      const response = await axios.get('/api/dictionaries/cities/all', { params });
      return response.data?.cities || [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Geo SSOT: создать город из формы склада (POST /api/dictionaries/cities) */
  async createCity(data) {
    try {
      const response = await axios.post('/api/dictionaries/cities', data);
      const result = this.handleResponse(response);
      if (result.success) return result.data?.city || result.data;
      throw new Error(result.error || 'Не удалось создать город');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createWarehouse(data) {
    try {
      // CRUD для складов находится в /api/warehouses, а словарь — в /api/dictionaries/warehouses (GET)
      // Здесь именно создание склада, используем /api/warehouses
      const response = await axios.post('/api/warehouses', data);
      const result = this.handleResponse(response);
      if (result.success) return result.data;
      throw new Error(result.error || 'Не удалось создать склад');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateWarehouse(id, data) {
    try {
      const url = this.replaceUrlParams('/api/warehouses/:id', { id });
      const response = await axios.put(url, data);
      const result = this.handleResponse(response);
      if (result.success) return result.data;
      throw new Error(result.error || 'Не удалось сохранить склад');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async repushLogisticsMirror() {
    try {
      const response = await axios.post('/api/warehouses/repush-logistics-mirror');
      const result = this.handleResponse(response);
      if (result.success) return result.data;
      throw new Error(result.error || 'Не удалось синхронизировать склады с LOGISTICS');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getRouteMap() {
    try {
      const response = await axios.get('/api/warehouses/route-map');
      const result = this.handleResponse(response);
      if (result.success) return result.data;
      throw new Error(result.error || 'Не удалось загрузить карту складов');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async rebuildRouteMap() {
    try {
      const response = await axios.post('/api/warehouses/route-map/rebuild');
      const result = this.handleResponse(response);
      if (result.success) return result.data;
      throw new Error(result.error || 'Не удалось пересобрать граф маршрутов');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteWarehouse(id, options = {}) {
    try {
      const params = new URLSearchParams();
      if (options.force) params.set('force', 'true');
      if (options.acknowledge_critical) params.set('acknowledge_critical', 'true');
      if (options.background) params.set('background', 'true');
      const qs = params.toString();
      const url = this.replaceUrlParams(`/api/warehouses/:id${qs ? `?${qs}` : ''}`, { id });
      const response = await axios.delete(url, options.body ? { data: options.body } : undefined);
      const result = this.handleResponse(response);
      if (result.success) return result;
      throw new Error(result.error || 'Не удалось удалить склад');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getWarehouseDeleteImpact(id) {
    try {
      const url = this.replaceUrlParams('/api/warehouses/:id/delete-impact', { id });
      const response = await axios.get(url);
      const result = this.handleResponse(response);
      if (result.success) return result.data;
      throw new Error(result.error || 'Не удалось загрузить влияние удаления');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getWarehouseDeleteJobStatus(id, jobId) {
    try {
      const url = this.replaceUrlParams('/api/warehouses/:id/delete-job/:jobId', { id, jobId });
      const response = await axios.get(url);
      const result = this.handleResponse(response);
      if (result.success) return result.data;
      throw new Error(result.error || 'Статус удаления недоступен');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async linkSupplierWarehouse({ warehouseId, supplierId, externalWarehouseId, isActive = true, settings = {} }) {
    try {
      const url = `/api/warehouses/${warehouseId}/suppliers/${supplierId}`;
      const payload = {
        external_warehouse_id: externalWarehouseId,
        is_active: isActive,
        settings,
      };
      const response = await axios.post(url, payload);
      const result = this.handleResponse(response);
      if (result.success) return true;
      throw new Error(result.error || 'Не удалось привязать склад поставщика');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getSupplierRoutes(warehouseId) {
    try {
      const response = await axios.get(`/api/warehouses/${warehouseId}/supplier-routes`);
      const result = this.handleResponse(response);
      if (result.success) return result.data || [];
      throw new Error(result.error || 'Не удалось загрузить маршруты');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getInboundRoutes(warehouseId) {
    try {
      const response = await axios.get(`/api/warehouses/${warehouseId}/inbound-routes`);
      const result = this.handleResponse(response);
      if (result.success) return result.data || [];
      throw new Error(result.error || 'Не удалось загрузить входящие маршруты');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async replaceSupplierRoutesBulk(warehouseId, payload) {
    try {
      const response = await axios.post(`/api/warehouses/${warehouseId}/supplier-routes/bulk`, payload);
      const result = this.handleResponse(response);
      if (result.success) return result.data || [];
      throw new Error(result.error || 'Не удалось сохранить маршруты');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getWarehouseStock(id, params = {}) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.WAREHOUSE_STOCK, { id });
      // ❌ НЕ КЭШИРУЕМ: Наличие - критически важные данные
      const response = await axios.get(url, {
        params,
        cacheTtlMs: 0 // Явно отключаем кэширование
      });
      const result = this.handleResponse(response);

      if (result.success) {
        return {
          stock: result.data || [],
          pagination: result.pagination || {},
          total: result.pagination?.total || 0
        };
      }

      throw new Error(result.error || 'Не удалось загрузить остатки склада');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getWarehouseMovements(id, params = {}) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.WAREHOUSE_MOVEMENTS, { id });
      const response = await axios.get(url, { params });
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось загрузить движения по складу');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateStock(stockData) {
    try {
      const response = await axios.post(API_ENDPOINTS.WAREHOUSE_UPDATE_STOCK, stockData);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось обновить остаток');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async transferStock(transferData) {
    try {
      const response = await axios.post(API_ENDPOINTS.WAREHOUSE_TRANSFER, transferData);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось выполнить перемещение остатков');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ===== Компоненты мульти-склада =====
  async getComponents(warehouseId) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.WAREHOUSE_DETAILS, { id: warehouseId }) + '/components';
      const response = await axios.get(url);
      const result = this.handleResponse(response);
      if (result.success) return result.data || [];
      throw new Error(result.error || 'Не удалось загрузить состав товара');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async addComponent(warehouseId, componentWarehouseId, weight = 1) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.WAREHOUSE_DETAILS, { id: warehouseId }) + '/components';
      const response = await axios.post(url, { source_warehouse_id: componentWarehouseId, weight });
      const result = this.handleResponse(response);
      if (result.success) return true;
      throw new Error(result.error || 'Не удалось добавить компонент');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async removeComponent(warehouseId, componentWarehouseId) {
    try {
      const base = this.replaceUrlParams(API_ENDPOINTS.WAREHOUSE_DETAILS, { id: warehouseId });
      const url = `${base}/components/${componentWarehouseId}`;
      const response = await axios.delete(url);
      const result = this.handleResponse(response);
      if (result.success) return true;
      throw new Error(result.error || 'Не удалось удалить компонент');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async validateExternalWarehouseId(supplierId, externalWarehouseId, excludeWarehouseId) {
    try {
      const response = await axios.get('/api/warehouses/validate/external-id', {
        params: {
          supplier_id: supplierId,
          external_warehouse_id: externalWarehouseId,
          exclude_warehouse_id: excludeWarehouseId,
        }
      });
      const result = this.handleResponse(response);
      if (result.success) return result.data;
      throw new Error(result.error || 'Не удалось проверить внешний идентификатор');
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

// =====================================
// SUPPLIERS API (УНИФИЦИРОВАННЫЙ)
// =====================================

class SuppliersAPI extends BaseAPIService {
  /**
   * Получение всех поставщиков компании
   */
  async getSuppliers(params = {}) {
    try {
      const response = await SUPPLIERS_API.getSuppliers(params);
      const result = this.handleResponse(response);
      // ✅ ИСПРАВЛЕНО: Возвращаем полную структуру { data: [...], pagination: {...} }
      // SuppliersPage и другие потребители ожидают suppliersResp.data (массив) и suppliersResp.pagination
      return result;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение складов поставщика
   */
  async getWarehouses(supplierId) {
    try {
      const response = await SUPPLIERS_API.getWarehouses(supplierId);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение всех товаров
   */
  async getAllProducts(supplierId, params = {}) {
    try {
      const response = await SUPPLIERS_API.getProducts(supplierId, params);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Создание поставщика (унифицированный CRUD)
   */
  async createSupplier(data) {
    try {
      const response = await axios.post('/api/suppliers', data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateSupplier(supplierId, data) {
    try {
      const response = await axios.put(`/api/suppliers/${supplierId}`, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async testSupplier(supplierId, data = {}) {
    try {
      const response = await SUPPLIERS_API.testSupplier(supplierId, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение всех брендов
   */
  async getAllBrands(supplierId, params = {}) {
    try {
      const response = await SUPPLIERS_API.getExternalBrands(supplierId, params);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Синхронизация складов
   */
  async syncWarehouses(supplierId) {
    try {
      const response = await SUPPLIERS_API.syncWarehouses(supplierId);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Синхронизация товаров
   */
  async syncProducts(supplierId, data) {
    try {
      const response = await SUPPLIERS_API.syncProducts(supplierId, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async syncPositions(supplierId, data) {
    try {
      const response = await SUPPLIERS_API.syncPositions(supplierId, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Синхронизация цен
   */
  async syncPrices(supplierId, data) {
    try {
      const response = await SUPPLIERS_API.syncPrices(supplierId, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Синхронизация цен по брендам
   */
  async syncPricesByBrands(supplierId, data) {
    try {
      const response = await SUPPLIERS_API.syncPricesByBrands(supplierId, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Синхронизация остатков
   */
  async syncStocks(supplierId, warehouseId, data) {
    try {
      const response = await SUPPLIERS_API.syncStocks(supplierId, warehouseId, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async syncStocksAll(supplierId, data) {
    try {
      const response = await SUPPLIERS_API.syncStocksAll(supplierId, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Синхронизация характеристик
   */
  async syncSpecs(supplierId, data) {
    try {
      const response = await SUPPLIERS_API.syncSpecs(supplierId, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Правила импорта поставщика */
  async getImportRules(supplierId) {
    try {
      const response = await SUPPLIERS_API.getImportRules(supplierId);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async saveImportRules(supplierId, data) {
    try {
      const response = await SUPPLIERS_API.saveImportRules(supplierId, data);
      const result = this.handleResponse(response);
      try {
        axios.clearGetCache((k) => String(k).includes(`/api/suppliers/${supplierId}/import-rules`));
      } catch (_) {}
      return result;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteImportRules(supplierId) {
    try {
      const response = await SUPPLIERS_API.deleteImportRules(supplierId);
      const result = this.handleResponse(response);
      try {
        axios.clearGetCache((k) => String(k).includes(`/api/suppliers/${supplierId}/import-rules`));
      } catch (_) {}
      return result;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Сохранение учетных данных
   */
  async saveCredentials(supplierId, data) {
    try {
      const response = await SUPPLIERS_API.saveCredentials(supplierId, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение учетных данных
   */
  async getCredentials(supplierId) {
    try {
      const response = await SUPPLIERS_API.getCredentials(supplierId);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Defaults заказов (location / skl / ILN) — App SSOT → ORDERS sync
   */
  async saveOrderDefaults(supplierId, data) {
    try {
      const response = await SUPPLIERS_API.saveOrderDefaults(supplierId, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение товаров с фильтрацией
   */
  async getProducts(supplierId, params = {}, requestOptions = {}) {
    try {
      const response = await SUPPLIERS_API.getProducts(supplierId, params, requestOptions);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Фоновая догрузка тяжёлых полей для товаров поставщика */
  async getProductsBulkFields(supplierId, payload = {}, requestOptions = {}) {
    try {
      const response = await SUPPLIERS_API.getProductsBulkFields(supplierId, payload, requestOptions);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Удалить товар поставщика по externalId */
  async deleteSupplierProduct(supplierId, externalId) {
    try {
      const response = await SUPPLIERS_API.deleteProduct(supplierId, externalId);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Удалить любые товары поставщика (включая все связанные данные) */
  async deleteAnyProducts(supplierId, externalIds) {
    try {
      const response = await SUPPLIERS_API.deleteAnyProducts(supplierId, externalIds);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Удалить характеристики товаров поставщика */
  async deleteProductSpecs(supplierId, externalIds) {
    try {
      const response = await SUPPLIERS_API.deleteProductSpecs(supplierId, externalIds);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение ID товаров по фильтрам
   */
  async getProductIds(supplierId, params = {}) {
    try {
      const response = await SUPPLIERS_API.getProductIds(supplierId, params);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Быстрая статистика по товарам поставщика (всего/новые/к удалению)
   */
  async getProductsStats(supplierId) {
    try {
      const response = await SUPPLIERS_API.getProductsStats(supplierId);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Покрытие последней полной выгрузки поставщика.
   *
   * ProductsPage работает только через публичный api.suppliers, поэтому новые
   * методы низкоуровневого SUPPLIERS_API должны явно входить в этот контракт.
   */
  async getFeedCoverage(supplierId) {
    try {
      const response = await SUPPLIERS_API.getFeedCoverage(supplierId);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Скачивание характеристик поставщика
   */
  async downloadSupplierSpecs(supplierId, data) {
    try {
      const response = await SUPPLIERS_API.downloadSupplierSpecs(supplierId, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Зеркалирование медиа поставщика → FILES/S3
   */
  async mirrorSupplierMedia(supplierId, data) {
    try {
      const response = await SUPPLIERS_API.mirrorSupplierMedia(supplierId, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение подробных цен товара поставщика по складам
   */
  async getProductPrices(supplierId, externalId) {
    try {
      const response = await SUPPLIERS_API.getProductPrices(supplierId, externalId);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение подробных остатков товара поставщика по складам
   */
  async getProductStocks(supplierId, externalId) {
    try {
      const response = await SUPPLIERS_API.getProductStocks(supplierId, externalId);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение характеристик товара
   */
  async getProductSpecs(supplierId, code) {
    try {
      const response = await SUPPLIERS_API.getProductSpecs(supplierId, code);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение живых характеристик напрямую у адаптера поставщика.
   */
  async getProductSpecsLive(supplierId, code) {
    try {
      const response = await SUPPLIERS_API.getProductSpecsLive(supplierId, code);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Импорт или сопоставление товаров
   */
  async importOrMatch(supplierId, data) {
    try {
      const response = await SUPPLIERS_API.importOrMatch(supplierId, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение логов синхронизации
   */
  async getSyncLogs(supplierId, params = {}) {
    try {
      const response = await SUPPLIERS_API.getSyncLogs(supplierId, params);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение последних цен
   */
  async getLatestPrices(supplierId, params = {}) {
    try {
      const response = await SUPPLIERS_API.getLatestPrices(supplierId, params);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение последних остатков
   */
  async getLatestStocks(supplierId, params = {}) {
    try {
      const response = await SUPPLIERS_API.getLatestStocks(supplierId, params);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение статистики поставщика
   */
  async getStats(supplierId) {
    try {
      const response = await SUPPLIERS_API.getStats(supplierId);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение маппингов брендов
   */
  async getBrandMappings(supplierId) {
    try {
      const response = await SUPPLIERS_API.getBrandMappings(supplierId);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Сохранение маппинга бренда
   */
  async saveBrandMapping(supplierId, data) {
    try {
      const response = await SUPPLIERS_API.saveBrandMapping(supplierId, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteBrandMapping(supplierId, supplierBrand) {
    try {
      const response = await SUPPLIERS_API.deleteBrandMapping(supplierId, supplierBrand);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Экспорт товаров
   */
  async exportProducts(supplierId, data) {
    try {
      const response = await SUPPLIERS_API.exportProducts(supplierId, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Экспорт цен
   */
  async exportPrices(supplierId, data) {
    try {
      const response = await SUPPLIERS_API.exportPrices(supplierId, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Экспорт остатков
   */
  async exportStocks(supplierId, data) {
    try {
      const response = await SUPPLIERS_API.exportStocks(supplierId, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Сопоставление товаров
   */
  async matchProducts(supplierId, data) {
    try {
      const response = await SUPPLIERS_API.matchProducts(supplierId, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Очистка кэша
   */
  async clearCache(supplierId) {
    try {
      const response = await SUPPLIERS_API.clearCache(supplierId);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Удаление поставщика
   */
  async deleteSupplier(supplierId) {
    try {
      const response = await axios.delete(`/api/suppliers/${supplierId}`);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

}

// =====================================
// MARKETPLACES API
// =====================================
class MarketplacesAPI extends BaseAPIService {
  async getMarketplaces(params = {}) {
    try {
      // ✅ КЭШИРУЕМ: Маркетплейсы - средний кэш (2 минуты)
      const response = await axios.get(API_ENDPOINTS.MARKETPLACES, {
        params,
        cacheTtlMs: 2 * 60 * 1000
      });
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось загрузить маркетплейсы');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getMarketplaceOrders(id, params = {}) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.MARKETPLACE_ORDERS, { id });
      const response = await axios.get(url, { params });
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось загрузить заказы маркетплейса');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async connectMarketplace(marketplaceData) {
    try {
      const response = await axios.post(API_ENDPOINTS.MARKETPLACE_CONNECT, marketplaceData);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось подключить маркетплейс');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async testMarketplace(id) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.MARKETPLACE_TEST, { id });
      const response = await axios.post(url);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось проверить подключение к маркетплейсу');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createMarketplace(data) {
    // Alias для connectMarketplace, для единообразия
    return this.connectMarketplace(data);
  }

  async updateMarketplace(id, data) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.MARKETPLACE_DETAILS, { id });
      const response = await axios.put(url, data);
      const result = this.handleResponse(response);
      if (result.success) return result.data;
      throw new Error(result.error || 'Не удалось сохранить настройки маркетплейса');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteMarketplace(id) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.MARKETPLACE_DETAILS, { id });
      const response = await axios.delete(url);
      const result = this.handleResponse(response);
      if (result.success) return result;
      throw new Error(result.error || 'Не удалось отключить маркетплейс');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async syncMarketplace(id, payload = { sync_type: 'full' }) {
    try {
      // Конструируем URL на основе DETAILS
      const base = this.replaceUrlParams(API_ENDPOINTS.MARKETPLACE_DETAILS, { id });
      const url = `${base}/sync`;
      const response = await axios.post(url, payload);
      const result = this.handleResponse(response);
      if (result.success) return result.data || result;
      throw new Error(result.error || 'Не удалось синхронизировать маркетплейс');
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

// =====================================
// SYNC API
// =====================================
class SyncAPI extends BaseAPIService {
  /** @deprecated Используйте api.marketplaces.syncMarketplace или api.suppliers.syncStocks */
  async syncStock(data) {
    const marketplaceId = data?.marketplace_id ?? data?.marketplaceId;
    if (marketplaceId) {
      const base = this.replaceUrlParams(API_ENDPOINTS.MARKETPLACE_DETAILS, { id: marketplaceId });
      const response = await axios.post(`${base}/sync`, { sync_type: 'stocks' });
      const result = this.handleResponse(response);
      if (result.success) return result.data || result;
      throw new Error(result.error || 'Не удалось синхронизировать остатки маркетплейса');
    }
    throw new Error(
      'POST /api/sync/stock устарел. Остатки поставщиков: «Поставщики» → sync/stocks; маркетплейсы: /api/marketplaces/:id/sync',
    );
  }

  async syncOrders(_data) {
    throw new Error('Синхронизация заказов выполняется только в модуле ORDERS.');
  }

  async getSyncById(id) {
    try {
      const url = this.replaceUrlParams('/api/sync/:id', { id });
      const response = await axios.get(url);
      const result = this.handleResponse(response);
      if (result.success) return result.data;
      throw new Error(result.error || 'Не удалось загрузить данные синхронизации');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getSyncLogs(params = {}) {
    try {
      const response = await axios.get(API_ENDPOINTS.SYNC_LOGS, { params });
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось загрузить журнал синхронизации');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getSyncStatus() {
    try {
      const response = await axios.get(API_ENDPOINTS.SYNC_STATUS);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось загрузить статус синхронизации');
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

// =====================================
// ANALYTICS API
// =====================================
class AnalyticsAPI extends BaseAPIService {
  async getDashboard() {
    try {
      const response = await axios.get(API_ENDPOINTS.ANALYTICS_DASHBOARD);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось загрузить данные дашборда');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getDashboardSettings(deviceType = 'desktop') {
    try {
      const response = await axios.get(API_ENDPOINTS.ANALYTICS_DASHBOARD_SETTINGS, {
        params: { device_type: deviceType }
      });
      const result = this.handleResponse(response);
      if (result.success) {
        return result.data;
      }
      throw new Error(result.error || 'Не удалось загрузить настройки дашборда');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async saveDashboardSettings(layouts, widgets, deviceType = 'desktop') {
    try {
      const response = await axios.post(API_ENDPOINTS.ANALYTICS_DASHBOARD_SETTINGS, {
        layouts,
        widgets,
        device_type: deviceType
      });
      const result = this.handleResponse(response);
      if (result.success) {
        return result.data;
      }
      throw new Error(result.error || 'Не удалось сохранить настройки дашборда');
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

// =====================================
// BILLING API
// =====================================
class BillingAPI extends BaseAPIService {
  async getTariffs() {
    try {
      const response = await axios.get(API_ENDPOINTS.BILLING_TARIFFS);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось загрузить тарифы');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getUsage() {
    try {
      const response = await axios.get(API_ENDPOINTS.BILLING_USAGE);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось загрузить данные использования');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getSubscriptionInfo() {
    try {
      const response = await axios.get(API_ENDPOINTS.BILLING_SUBSCRIPTION_INFO);
      const result = this.handleResponse(response);
      if (result.success) {
        return result.data;
      }
      throw new Error(result.error || 'Не удалось загрузить данные подписки');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getTransactions(params = {}) {
    try {
      const response = await axios.get(API_ENDPOINTS.BILLING_TRANSACTIONS, { params });
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось загрузить транзакции');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async changeTariff(tariffId) {
    try {
      const response = await axios.post(API_ENDPOINTS.BILLING_CHANGE_TARIFF, { tariff_id: tariffId });
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось сменить тариф');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createPaymentIntent(tariffId) {
    try {
      const response = await axios.post(API_ENDPOINTS.BILLING_CREATE_PAYMENT_INTENT, { tariff_id: tariffId });
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось создать платёжное намерение');
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

// =====================================
// USERS API
// =====================================
class UsersAPI extends BaseAPIService {
  async getUsers(params = {}) {
    try {
      // ✅ КЭШИРУЕМ: Пользователи - средний кэш (2 минуты)
      const response = await axios.get(API_ENDPOINTS.USERS, {
        params,
        cacheTtlMs: 2 * 60 * 1000
      });
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось загрузить пользователей');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getUser(id) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.USER_DETAILS, { id });
      const response = await axios.get(url);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось загрузить пользователя');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createUser(userData) {
    try {
      const response = await axios.post(API_ENDPOINTS.USERS, userData);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось создать пользователя');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateUser(id, userData) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.USER_DETAILS, { id });
      const response = await axios.put(url, userData);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось сохранить пользователя');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteUser(id) {
    try {
      const url = this.replaceUrlParams(API_ENDPOINTS.USER_DETAILS, { id });
      const response = await axios.delete(url);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось удалить пользователя');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Избранные элементы меню
  async getMenuFavorites() {
    try {
      const response = await axios.get('/api/users/favorites/menu', { silent401: true });
      const result = this.handleResponse(response);

      if (result.success) {
        // Поддержка старого формата (массив строк) и нового (массив объектов)
        if (result.data && result.data.length > 0 && typeof result.data[0] === 'string') {
          return result.data.map((menuKey, index) => ({ menuKey, order: index }));
        }
        return result.data || [];
      }

      throw new Error(result.error || 'Не удалось загрузить избранное в меню');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async addMenuFavorite(menuKey) {
    try {
      const response = await axios.post('/api/users/favorites/menu', { menuKey });
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось добавить в избранное');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async removeMenuFavorite(menuKey) {
    try {
      const response = await axios.delete(`/api/users/favorites/menu/${encodeURIComponent(menuKey)}`);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось удалить из избранного');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateMenuFavoritesOrder(favorites) {
    try {
      const response = await axios.put('/api/users/favorites/menu/order', { favorites });
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось сохранить порядок избранного');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Настройки навигации сайдбара (пресеты / порядок / collapse) — cross-device
  async getNavigationPreferences() {
    try {
      const response = await axios.get('/api/users/navigation-preferences', { silent401: true });
      const result = this.handleResponse(response);
      if (result.success) {
        return { success: true, data: result.data };
      }
      throw new Error(result.error || 'Не удалось загрузить настройки меню');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async saveNavigationPreferences(prefs) {
    try {
      const response = await axios.put('/api/users/navigation-preferences', prefs);
      const result = this.handleResponse(response);
      if (result.success) {
        return { success: true, data: result.data };
      }
      throw new Error(result.error || 'Не удалось сохранить настройки меню');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async resetNavigationPreferences() {
    try {
      const response = await axios.post('/api/users/navigation-preferences/reset');
      const result = this.handleResponse(response);
      if (result.success) {
        return { success: true, data: result.data };
      }
      throw new Error(result.error || 'Не удалось сбросить настройки меню');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async savePersonalNavPreset({ name, snapshot } = {}) {
    try {
      const response = await axios.post('/api/users/navigation-preferences/presets', { name, snapshot });
      const result = this.handleResponse(response);
      if (result.success) {
        return { success: true, data: result.data, presetId: result.presetId };
      }
      throw new Error(result.error || 'Не удалось сохранить пресет');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deletePersonalNavPreset(presetId) {
    try {
      const response = await axios.delete(
        `/api/users/navigation-preferences/presets/${encodeURIComponent(presetId)}`
      );
      const result = this.handleResponse(response);
      if (result.success) {
        return { success: true, data: result.data };
      }
      throw new Error(result.error || 'Не удалось удалить пресет');
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

// =====================================
// SETTINGS API
// =====================================
class SettingsAPI extends BaseAPIService {
  async getSettings(config = {}) {
    try {
      // ✅ Поддерживаем cacheTtlMs для отключения кэша (критично для настроек НДС)
      const response = await axios.get(API_ENDPOINTS.SETTINGS, config);
      const result = this.handleResponse(response);
      return result?.data || {};
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateSettings(payload) {
    try {
      const response = await axios.put(API_ENDPOINTS.SETTINGS, payload);
      const result = this.handleResponse(response);
      return result?.data || result;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getProfile() {
    try {
      const response = await axios.get(API_ENDPOINTS.SETTINGS_PROFILE);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось загрузить профиль');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateProfile(profileData) {
    try {
      const response = await axios.put(API_ENDPOINTS.SETTINGS_PROFILE, profileData);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось обновить профиль');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getIntegrations() {
    try {
      const response = await axios.get(API_ENDPOINTS.SETTINGS_INTEGRATIONS);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось загрузить интеграции');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateIntegrations(integrationsData) {
    try {
      const response = await axios.put(API_ENDPOINTS.SETTINGS_INTEGRATIONS, integrationsData);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось обновить интеграции');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getNotifications() {
    try {
      const result = await notificationService.getCompanyPreferences();
      const mapped = { app: true, email: true, push: true, telegram: true };
      (result.company_preferences || []).forEach((pref) => {
        if (pref.channel in mapped) mapped[pref.channel] = pref.enabled !== false;
      });
      return mapped;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateNotifications(notificationsData) {
    try {
      const preferences = Object.entries(notificationsData || {}).map(([channel, enabled]) => ({
        channel,
        enabled: enabled !== false,
      }));
      await notificationService.updateCompanyPreferences(preferences);
      return this.getNotifications();
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getApiKeys() {
    try {
      const response = await axios.get(API_ENDPOINTS.SETTINGS_API_KEYS);
      const result = this.handleResponse(response);

      if (result.success) {
        return result.data;
      }

      throw new Error(result.error || 'Не удалось загрузить API-ключи');
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

// =====================================
// HEALTH API
// =====================================
class HealthAPI extends BaseAPIService {
  async getHealth() {
    try {
      const response = await axios.get(API_ENDPOINTS.HEALTH);
      return response.data; // health endpoints are plain JSON without {success}
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getLive() {
    try {
      const response = await axios.get(API_ENDPOINTS.HEALTH_LIVE);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getReady() {
    try {
      const response = await axios.get(API_ENDPOINTS.HEALTH_READY);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

// =====================================
// INTEGRATIONS API
// =====================================
class IntegrationsAPI extends BaseAPIService {
  /**
   * Тестирование подключения к поставщику (унифицировано)
   */
  async testSupplier({ supplierId, api_url, api_type = 'rs24', credentials = {} }) {
    try {
      // Если supplierId не передан, ошибка
      if (!supplierId) throw new Error('Не задан идентификатор поставщика');
      const response = await axios.post(`/api/suppliers/${supplierId}/test`, {
        api_url,
        api_type,
        credentials
      });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Сохранение учетных данных поставщика (унифицировано)
   * Примечание: если supplierId не передан, бэкенд создаст поставщика RS24 автоматически.
   */
  async saveSupplierCredentials({ supplierId, ...data }) {
    try {
      const response = await SUPPLIERS_API.saveCredentials(supplierId, data);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async startSync(type, options = {}) {
    const response = await axios.post('/api/integrations/sync/start', { type, ...options });
    return this.handleResponse(response);
  }
  async getSyncStatus(id) {
    const response = await axios.get(`/api/integrations/sync/status/${id}`);
    return this.handleResponse(response);
  }
  async getSyncLogs(id, params = {}) {
    const response = await axios.get(`/api/integrations/sync/logs/${id}`, { params });
    return this.handleResponse(response);
  }
  // Harvest schedule
  async scheduleHarvest({ supplier_id, method, cron_expression, options = {} }) {
    const response = await axios.post('/api/integrations/schedule-harvest', { supplier_id, method, cron_expression, options });
    return this.handleResponse(response);
  }

  // Методы для быстрого подключения поставщиков
  async testETM({ api_url, username, password }) {
    try {
      const response = await axios.post('/api/suppliers/test-connection', {
        supplier_type: 'etm',
        api_url,
        username,
        password
      });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async saveETM({ username, password, api_url, is_active = true }) {
    try {
      // ✅ ИСПРАВЛЕНО: Используем унифицированный endpoint /api/suppliers/credentials
      const response = await axios.post('/api/suppliers/credentials', {
        supplier_type: 'etm',
        username,
        password,
        api_url,
        is_active
      });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async testRS24({ api_url, username, password }) {
    try {
      const response = await axios.post('/api/suppliers/test-connection', {
        supplier_type: 'rs24',
        api_url,
        username,
        password
      });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

// =====================================
// HARVEST API
// =====================================
class HarvestAPI extends BaseAPIService {
  async start(payload) {
    try {
      const response = await axios.post('/api/harvest/start', payload);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }
  async getSessions(params = {}) {
    try {
      const response = await axios.get('/api/harvest/sessions', { params });
      const result = this.handleResponse(response);
      return result.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
  async getFiles(params = {}) {
    try {
      const response = await axios.get('/api/harvest/files', { params });
      const result = this.handleResponse(response);
      return result.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
  async getMethods() {
    try {
      const response = await axios.get('/api/harvest/methods');
      const result = this.handleResponse(response);
      return result.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
  async calculate(payload) {
    try {
      const response = await axios.post('/api/harvest/calculate', payload);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

// =====================================
// USER RESOURCES API
// =====================================
class UserResourcesAPI extends BaseAPIService {
  /**
   * Получение лимитов ресурсов пользователя
   */
  async getResourceLimits() {
    try {
      const response = await axios.get('/api/user-resources/limits');
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Обновление лимитов ресурсов
   */
  async updateResourceLimits(updates) {
    try {
      const response = await axios.put('/api/user-resources/limits', updates);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение статистики использования ресурсов
   */
  async getResourceUsage() {
    try {
      const response = await axios.get('/api/user-resources/usage');
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Создание нового процесса
   */
  async createProcess(processData) {
    try {
      const response = await axios.post('/api/user-resources/processes', processData);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Обновление прогресса процесса
   */
  async updateProcessProgress(processId, progressData) {
    try {
      const response = await axios.put(`/api/user-resources/processes/${processId}/progress`, progressData);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получение активных процессов пользователя
   */
  async getActiveProcesses() {
    try {
      const response = await axios.get('/api/user-resources/processes/active');
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Остановка процесса
   */
  async stopProcess(processId) {
    try {
      const response = await axios.delete(`/api/user-resources/processes/${processId}`);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Принудительно запустить процесс из очереди (pending)
   */
  async forceStartProcess(processId) {
    try {
      const response = await axios.post(`/api/user-resources/processes/${processId}/force-start`);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Остановить все активные процессы
   */
  async stopAllProcesses() {
    try {
      const response = await axios.post('/api/user-resources/processes/stop-all');
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Поставить процесс на паузу
   */
  async pauseProcess(processId, checkpointData = null) {
    try {
      const response = await axios.post(`/api/user-resources/processes/${processId}/pause`, { checkpointData });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Продолжить процесс после паузы
   */
  async resumeProcess(processId) {
    try {
      const response = await axios.post(`/api/user-resources/processes/${processId}/resume`);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Принудительная остановка всех процессов
   */
  async forceStopAllProcesses() {
    try {
      const response = await axios.post('/api/user-resources/processes/force-stop-all');
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Поставить все процессы на паузу
   */
  async pauseAllProcesses() {
    try {
      const response = await axios.post('/api/user-resources/processes/pause-all');
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Продолжить все процессы
   */
  async resumeAllProcesses() {
    try {
      const response = await axios.post('/api/user-resources/processes/resume-all');
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Изменить приоритет процесса
   */
  async updateProcessPriority(processId, priority, queuePosition = null) {
    try {
      const response = await axios.put(`/api/user-resources/processes/${processId}/priority`, {
        priority,
        queuePosition
      });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получить историю процессов
   */
  async getProcessHistory(filters = {}) {
    try {
      const response = await axios.get('/api/user-resources/history', { params: filters });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 🆕 Получить лимиты очереди для компании (по тарифу)
   */
  async getQueueLimits() {
    try {
      const response = await axios.get('/api/user-resources/queue/limits');
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 🆕 Проверить можно ли запустить новый процесс
   */
  async checkQueueAvailability() {
    try {
      const response = await axios.get('/api/user-resources/queue/check');
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

// =====================================
// SUPPLIER BUILDER API
// =====================================
class SupplierBuilderAPI extends BaseAPIService {
  /**
   * Получить схему конфигурации адаптера
   */
  async getSchema() {
    try {
      const response = await axios.get('/api/supplier-builder/schema');
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Тестировать конфигурацию адаптера
   */
  async testConfig(config) {
    try {
      const response = await axios.post('/api/supplier-builder/test', config);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Предварительный просмотр данных API
   */
  async previewData(config, endpoint = 'positions') {
    try {
      const response = await axios.post('/api/supplier-builder/preview', {
        config,
        endpoint
      });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Создать адаптер из конфигурации
   */
  async createAdapter(config) {
    try {
      const response = await axios.post('/api/supplier-builder/create', config);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Получить предложения для маппинга полей
   */
  async getFieldSuggestions() {
    try {
      const response = await axios.get('/api/supplier-builder/field-suggestions');
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Анализировать ответ API для автоматического маппинга
   */
  async analyzeResponse(responseData) {
    try {
      const response = await axios.post('/api/supplier-builder/analyze-response', {
        response_data: responseData
      });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

// =====================================
// СОЗДАНИЕ ЭКЗЕМПЛЯРОВ API
// =====================================
const authAPI = new AuthAPI();
const productsAPI = new ProductsAPI();
const pricesAPI = new PricesAPI();
const currenciesAPI = new CurrenciesAPI();
const ordersAPI = new OrdersAPI();
const warehousesAPI = new WarehousesAPI();
const suppliersAPI = new SuppliersAPI();
const marketplacesAPI = new MarketplacesAPI();
const syncAPI = new SyncAPI();
const analyticsAPI = new AnalyticsAPI();
const billingAPI = new BillingAPI();
const usersAPI = new UsersAPI();
const settingsAPI = new SettingsAPI();
const healthAPI = new HealthAPI();
const integrationsAPI = new IntegrationsAPI();
const harvestAPI = new HarvestAPI();
const userResourcesAPI = new UserResourcesAPI();
const supplierBuilderAPI = new SupplierBuilderAPI();

// =====================================
// ЕДИНЫЙ API ОБЪЕКТ
// =====================================
export const api = {
  auth: authAPI,
  products: productsAPI,
  prices: pricesAPI,
  currencies: currenciesAPI,
  productGroups: productGroupsService,
  orders: ordersAPI,
  warehouses: warehousesAPI,
  suppliers: suppliersAPI, // Унифицированный API для всех поставщиков
  marketplaces: marketplacesAPI,
  sync: syncAPI,
  analytics: analyticsAPI,
  billing: billingAPI,
  users: usersAPI,
  settings: settingsAPI,
  health: healthAPI,
  integrations: integrationsAPI,
  harvest: harvestAPI,
  userResources: userResourcesAPI,
  supplierBuilder: supplierBuilderAPI,
  companies: companiesService,
  notifications: notificationService,

  // Прямые HTTP методы для совместимости
  get: async (url, config) => {
    const response = await axios.get(url, config);
    return response;
  },
  post: async (url, data, config) => {
    const response = await axios.post(url, data, config);
    return response;
  },
  put: async (url, data, config) => {
    const response = await axios.put(url, data, config);
    return response;
  },
  patch: async (url, data, config) => {
    const response = await axios.patch(url, data, config);
    return response;
  },
  delete: async (url, config) => {
    const response = await axios.delete(url, config);
    return response;
  },
};

// =====================================
// ЭКСПОРТ ОТДЕЛЬНЫХ API ДЛЯ СОВМЕСТИМОСТИ
// =====================================
export {
  analyticsAPI, authAPI, billingAPI, harvestAPI, healthAPI,
  integrationsAPI,
  marketplacesAPI, ordersAPI, productsAPI, settingsAPI, suppliersAPI, syncAPI, usersAPI, warehousesAPI, userResourcesAPI
};

// =====================================
// ЭКСПОРТ ПО УМОЛЧАНИЮ
// =====================================
export default api;
